import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const WL_CODE_KEY = "alphasync_wl_code";
function ensureWlCode() {
  let c = localStorage.getItem(WL_CODE_KEY);
  if (!c) {
    c = Math.random().toString(36).slice(2, 8).toUpperCase();
    localStorage.setItem(WL_CODE_KEY, c);
  }
  return c;
}

export function usePwa() {
  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);
  const [canInstall, setCanInstall] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const deferred = useRef(null);
  const pushSupported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    const onBip = (e) => { e.preventDefault(); deferred.current = e; setCanInstall(true); };
    const onInstalled = () => { deferred.current = null; setCanInstall(false); toast.success("ALPHASYNC installed to your device"); };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onBip); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              toast("New version available", {
                description: "Refresh to get the latest ALPHASYNC.",
                duration: Infinity,
                action: {
                  label: "Refresh",
                  onClick: () => {
                    navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), { once: true });
                    nw.postMessage({ type: "SKIP_WAITING" });
                  },
                },
              });
            }
          });
        });
      })
      .catch(() => {});
  }, []);

  // Init push state from existing subscription
  useEffect(() => {
    if (!pushSupported) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setPushEnabled(!!sub && Notification.permission === "granted"))
      .catch(() => {});
  }, [pushSupported]);

  const promptInstall = useCallback(async () => {
    const d = deferred.current;
    if (!d) return;
    d.prompt();
    try { await d.userChoice; } catch (e) { /* dismissed */ }
    deferred.current = null;
    setCanInstall(false);
  }, []);

  const enablePush = useCallback(async () => {
    if (!pushSupported) { toast.error("Push notifications are not supported on this device/browser"); return; }
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications permission denied"); setPushBusy(false); return; }
      const reg = await navigator.serviceWorker.ready;
      const res = await fetch(`${API}/push/public-key`);
      const { public_key } = await res.json();
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(public_key) });
      }
      await fetch(`${API}/push/subscribe`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subscription: sub.toJSON(), code: ensureWlCode() }),
      });
      setPushEnabled(true);
      toast.success("Push alerts enabled — you'll be alerted for STRONG setups (your watchlist is prioritised)");
    } catch (e) {
      toast.error("Could not enable push alerts");
    } finally {
      setPushBusy(false);
    }
  }, [pushSupported]);

  const disablePush = useCallback(async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await fetch(`${API}/push/unsubscribe`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        }).catch(() => {});
        await sub.unsubscribe();
      }
      setPushEnabled(false);
      toast.success("Push alerts disabled");
    } catch (e) {
      toast.error("Could not disable push alerts");
    } finally {
      setPushBusy(false);
    }
  }, []);

  return { isOffline, canInstall, promptInstall, pushSupported, pushEnabled, pushBusy, enablePush, disablePush };
}
